import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReglementFournisseurController from './controllers/reglement-fournisseur-controller';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';

// Amounts in the mock are shown as "26 224,00 DT" (2 decimals, French grouping).
const fmt = (n) =>
  `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DT`;

const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';

// Deterministic gradient per supplier name for avatars.
const AVATAR_GRADIENTS = [
  'from-emerald-500 to-teal-600',
  'from-sky-500 to-indigo-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-violet-500 to-purple-600',
  'from-cyan-500 to-blue-600',
];
const avatarGradient = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 997;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
};

// Solde status: credit (supplier owes us), due (we owe supplier), balanced.
const soldeStatus = (solde) => {
  if (solde > 0.0001) return 'credit';
  if (solde < -0.0001) return 'du';
  return 'equilibre';
};

const ConsommationFournisseurPage = () => {
  const { hasPermission } = usePermissionViewModel('consommation_fournisseur');
  const canRead = hasPermission('read', 'consommation_fournisseur');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ReglementFournisseurController.getConsommation();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement de la consommation fournisseurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canRead) load();
  }, [canRead, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.fournisseur?.name || '').toLowerCase().includes(q));
  }, [rows, search]);

  // Global totals + status counts.
  const totals = useMemo(() => {
    let consommation = 0;
    let avances = 0;
    let solde = 0;
    let credit = 0;
    let du = 0;
    let equilibre = 0;
    rows.forEach((r) => {
      consommation += Number(r.consommation || 0);
      avances += Number(r.avances || 0);
      solde += Number(r.solde || 0);
      const st = soldeStatus(Number(r.solde || 0));
      if (st === 'credit') credit += 1;
      else if (st === 'du') du += 1;
      else equilibre += 1;
    });
    return { consommation, avances, solde, credit, du, equilibre };
  }, [rows]);

  if (!canRead) {
    return (
      <div className="p-8 text-center text-gray-500">
        Vous n'avez pas la permission de consulter cette page.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Consommation Fournisseurs</h1>
            <p className="text-sm text-gray-500 mt-1">
              Suivi en temps réel : Consommation (ventes) · Avances · Solde
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un fournisseur…"
                className="w-full sm:w-64 pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <svg className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
            </div>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Actualiser
            </button>
          </div>
        </div>

        {/* Global stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <StatCard
            label="CONSOMMATION GLOBALE"
            value={fmt(totals.consommation)}
            iconBg="bg-amber-100"
            icon={
              <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
          />
          <StatCard
            label="AVANCES GLOBALES"
            value={fmt(totals.avances)}
            iconBg="bg-blue-100"
            icon={
              <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            }
          />
          <StatCard
            label="SOLDE GLOBAL"
            value={fmt(totals.solde)}
            iconBg="bg-emerald-100"
            icon={
              <svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
            }
          />
        </div>

        {/* Status summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <SummaryPill
            count={totals.credit}
            label="Crédit fournisseur"
            wrap="bg-emerald-50 border-emerald-100"
            text="text-emerald-700"
            icon={
              <svg className="h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <SummaryPill
            count={totals.du}
            label="À régler"
            wrap="bg-rose-50 border-rose-100"
            text="text-rose-700"
            icon={
              <svg className="h-6 w-6 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <SummaryPill
            count={totals.equilibre}
            label="Équilibre"
            wrap="bg-gray-50 border-gray-200"
            text="text-gray-700"
            icon={
              <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
              </svg>
            }
          />
        </div>

        {/* Supplier cards grid */}
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-64 bg-white rounded-2xl border border-gray-100 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-gray-500">
            Aucun fournisseur trouvé.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((r) => (
              <SupplierCard key={r.fournisseurId} row={r} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon, iconBg }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center justify-between">
    <div>
      <p className="text-[11px] font-semibold tracking-wide text-gray-400">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    </div>
    <div className={`h-11 w-11 rounded-xl ${iconBg} flex items-center justify-center`}>{icon}</div>
  </div>
);

const SummaryPill = ({ count, label, icon, wrap, text }) => (
  <div className={`rounded-2xl border ${wrap} p-5 flex items-center gap-4`}>
    {icon}
    <div>
      <p className={`text-2xl font-bold ${text}`}>{count}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  </div>
);

const SupplierCard = ({ row }) => {
  const name = row.fournisseur?.name || `Fournisseur #${row.fournisseurId}`;
  const status = soldeStatus(Number(row.solde || 0));
  const solde = Number(row.solde || 0);

  const statusStyles = {
    credit: { wrap: 'bg-emerald-50', text: 'text-emerald-600', soldeBox: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', label: 'Crédit' },
    du: { wrap: 'bg-rose-50', text: 'text-rose-600', soldeBox: 'bg-rose-50', badge: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500', label: 'Dû' },
    equilibre: { wrap: 'bg-gray-50', text: 'text-gray-600', soldeBox: 'bg-gray-50', badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400', label: 'Équilibre' },
  }[status];

  const borderColor = status === 'credit' ? 'border-emerald-100' : status === 'du' ? 'border-rose-100' : 'border-gray-100';

  return (
    <div className={`bg-white rounded-2xl border ${borderColor} shadow-sm overflow-hidden`}>
      <div className="p-5">
        {/* Card header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`h-10 w-10 rounded-full bg-gradient-to-br ${avatarGradient(name)} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
              {initials(name)}
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 truncate">{name}</h3>
              <p className="text-xs text-gray-400">{row.nbProduits} produit(s)</p>
            </div>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border border-emerald-200 text-emerald-600">
            actif
          </span>
        </div>

        {/* Consommation row */}
        <div className="flex items-center justify-between py-2.5 border-b border-dashed border-gray-100">
          <span className="inline-flex items-center gap-2 text-sm text-gray-600">
            <span className="h-2 w-2 rounded-full bg-orange-400" />
            Consommation
          </span>
          <span className="text-sm font-bold text-orange-500">{fmt(row.consommation)}</span>
        </div>

        {/* Avances row */}
        <div className="flex items-center justify-between py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-gray-600">
            <span className="h-2 w-2 rounded-full bg-blue-400" />
            Avances
          </span>
          <span className="text-sm font-bold text-blue-500">{fmt(row.avances)}</span>
        </div>

        {/* Solde box */}
        <div className={`mt-3 rounded-xl ${statusStyles.soldeBox} p-4`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold tracking-wide text-gray-400">SOLDE</p>
              <p className={`text-xl font-bold ${statusStyles.text} mt-0.5`}>
                {solde > 0 ? '+' : ''}{fmt(solde)}
              </p>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusStyles.badge}`}>
              <span className={`h-2 w-2 rounded-full ${statusStyles.dot}`} />
              {statusStyles.label}
            </span>
          </div>
        </div>
      </div>

      {/* Footer note */}
      <div className="px-5 pb-4 -mt-1">
        {status === 'credit' && (
          <p className="text-xs text-emerald-600">Le fournisseur vous doit {fmt(solde)}</p>
        )}
        {status === 'du' && (
          <p className="text-xs text-rose-600">Vous devez {fmt(Math.abs(solde))} au fournisseur</p>
        )}
        {status === 'equilibre' && (
          <p className="text-xs text-gray-400">Compte équilibré</p>
        )}
      </div>
    </div>
  );
};

export default ConsommationFournisseurPage;
